import { IBannerData } from '../../../../interfaces/Website/Banners/IBanners'

export function getQueryParamsManually(url: string) {
  const params: Record<string, string> = {}
  const queryStringStartIndex = url.indexOf('?')

  if (queryStringStartIndex === -1 || queryStringStartIndex === url.length - 1) {
    return params
  }

  const queryString = url.substring(queryStringStartIndex + 1)

  const pairs = queryString.split('&')

  for (const pair of pairs) {
    if (!pair) {
      continue
    }

    const parts = pair.split('=')
    const key = decodeURIComponent(parts[0] || '') // Decodifica a chave, lida com chaves vazias
    const value = decodeURIComponent(parts.slice(1).join('=') || '') // Decodifica o valor, lida com múltiplos '='

    params[key] = value
  }

  return params
}

/**
 * Banner que já existe no servidor. Banner novo carrega um id temporário negativo, e é
 * `id > 0` — não a veracidade do id — que separa `create` de `update`.
 */
export function isPersistedBanner(id: number): boolean {
  return id > 0
}

/**
 * Id temporário para um banner novo: negativo, para nunca colidir com um id do servidor,
 * e menor que qualquer um já em uso, para não colidir com outro banner novo. Antes todo
 * banner novo nascia com `id: 0`, então dois deles dividiam a mesma `key` do React e o
 * mesmo id de sortable — arrastar um movia o outro.
 */
export function nextTemporaryBannerId(banners: IBannerData[]): number {
  return Math.min(0, ...banners.map(({ id }) => id)) - 1
}

// Campos comparados para decidir se um banner precisa ir para a API. A lista é
// explícita de propósito: um JSON.stringify do objeto inteiro dependeria da ordem das
// chaves e entraria em campos que o servidor devolve mas ninguém edita (`order`),
// marcando tudo como sujo.
const COMPARED_FIELDS: (keyof IBannerData)[] = [
  'title',
  'subtitle',
  'text',
  'video_url',
  'video_url_mobile',
  'cta_text',
  'cta_link',
  'title_color',
  'subtitle_color',
  'tag_presencial_color',
  'tag_online_color',
  'tag_online_ao_vivo_color',
  'tag_presencial_text_color',
  'tag_online_text_color',
  'tag_online_ao_vivo_text_color',
  'button_text_color',
  'gradient_color',
  'button_color'
]

// Identidade por instância de `File`. Um arquivo não tem id nem é comparável por valor,
// então a assinatura precisa de um token estável por arquivo escolhido: com um marcador
// fixo ("new-file") o banner que subiu num save parcial nunca voltaria a bater com nada
// e seria reenviado para sempre. Com o token, ele para de contar como sujo — e trocar a
// imagem por outra volta a contar.
const fileTokens = new WeakMap<File, string>()
let lastFileToken = 0

function fileToken(file: File): string {
  const known = fileTokens.get(file)

  if (known) {
    return known
  }

  lastFileToken += 1
  const token = `file:${lastFileToken}`
  fileTokens.set(file, token)

  return token
}

function imageSignature(image: IBannerData['image']): string {
  // Imagem recém-escolhida é um `File`: conta como alteração porque não existe "mesmo
  // arquivo" para comparar com a URL que veio do servidor.
  return image instanceof File ? fileToken(image) : (image ?? '')
}

/**
 * Assinatura comparável de um banner. Salvar compara a assinatura atual com a do
 * último `fetch` e envia só o que mudou — antes a tela mandava um multipart por banner
 * da home a cada clique em "Salvar alterações", e a rajada levava 429 do rate limit.
 */
export function bannerSignature(banner: IBannerData): string {
  return JSON.stringify([
    ...COMPARED_FIELDS.map((field) => String(banner[field] ?? '')),
    Number(!!banner.is_active),
    Number(!!banner.is_gradient_active),
    [...(banner.tags ?? [])].sort().join(','),
    imageSignature(banner.image),
    imageSignature(banner.image_mobile)
  ])
}

/**
 * Executa as tarefas com no máximo `limit` em voo. Substitui o `Promise.all` sobre a
 * lista inteira: com 20 banners ele disparava 20 uploads multipart no mesmo instante e
 * o `throttle` da API respondia 429 para boa parte deles.
 */
export async function runWithConcurrency<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
  const results: T[] = new Array(tasks.length)
  let next = 0

  const worker = async () => {
    while (next < tasks.length) {
      const index = next
      next += 1
      results[index] = await tasks[index]()
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker))

  return results
}

/**
 * O HTML de um editor sem texto. O Quill nunca fica vazio de verdade — apagar tudo deixa um
 * parágrafo com `<br>` —, e gravar isso faria o site desenhar um título em branco com altura.
 */
export function normalizeRichText(html: string): string {
  return html.replace(/<(p|br|\/p)[^>]*>|\s|&nbsp;/g, '') === '' ? '' : html
}

/**
 * Completa o `https://` de um endereço digitado sem ele (`www.abra.com.br`, `abra.com.br/cursos`).
 * A API valida com a regra `url`, que exige o protocolo, e era aí que um link certo para qualquer
 * pessoa voltava recusado. Vazio, com protocolo (`http://`) ou âncora/caminho do próprio
 * site (`#`, `/`) fica como está, e texto que não parece endereço (ou é e-mail) também.
 */
export function withUrlScheme(value: string): string {
  const trimmed = value.trim()

  // O protocolo é `algo://`, que é o que a regra `url` da API aceita. Só `algo:` não basta:
  // `meusite.com:8080` é host com porta e também precisa do `https://`.
  if (!trimmed || /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) || /^[/#]/.test(trimmed)) {
    return trimmed
  }

  // Só o que parece um endereço (`dominio.algo`, sem espaço). Um texto qualquer fica como foi
  // digitado: com `https://` na frente ele parecia um link e escondia o erro de quem escreveu.
  // E-mail também fica: `https://nome@abra.com.br` seria um link para o site, não para a pessoa.
  if (/\s|@/.test(trimmed) || !/^[^/]+\.[^/.]+/.test(trimmed)) {
    return trimmed
  }

  return `https://${trimmed}`
}
